/**
 * Settings store (spec sections 37, 38).
 *
 * Settings are small, synchronous and non-binary, so localStorage is the
 * right tool here. Video never touches localStorage.
 *
 * Deliberate choice: there is no "API key" field in the UI. Automated
 * providers are configured with a Worker URL, and the secret lives in
 * the Worker environment. The only credential field is an optional,
 * clearly-labelled operator note - never a secret.
 */

import { DEFAULT_SETTINGS, STORAGE } from '../core/config.js';

let cache = null;

export function loadSettings() {
  if (cache) return cache;
  const base = { ...DEFAULT_SETTINGS };
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE.localStorageKey);
    if (raw) {
      const parsed = JSON.parse(raw);
      cache = { ...base, ...sanitise(parsed) };
      return cache;
    }
  } catch {
    /* corrupted settings should never brick the app */
  }
  cache = base;
  return cache;
}

export function saveSettings(patch) {
  const next = { ...loadSettings(), ...sanitise(patch) };
  cache = next;
  try {
    globalThis.localStorage?.setItem(STORAGE.localStorageKey, JSON.stringify(next));
  } catch {
    /* private mode / quota - keep the in-memory copy */
  }
  return next;
}

export function resetSettings() {
  cache = { ...DEFAULT_SETTINGS };
  try {
    globalThis.localStorage?.removeItem(STORAGE.localStorageKey);
  } catch {
    /* ignore */
  }
  return cache;
}

export function getSetting(key) {
  return loadSettings()[key];
}

/** Coerce stored values back to the expected shape. Exported for tests. */
export function sanitiseSettings(input) {
  const out = {};
  for (const [key, fallback] of Object.entries(DEFAULT_SETTINGS)) {
    const value = input?.[key];
    if (value === undefined) continue;
    if (typeof fallback === 'number') {
      const n = Number(value);
      out[key] = Number.isFinite(n) ? n : fallback;
    } else if (typeof fallback === 'boolean') {
      out[key] = Boolean(value);
    } else {
      out[key] = String(value);
    }
  }
  return out;
}

const sanitise = sanitiseSettings;

/**
 * Provider configuration (spec section 37/38).
 *
 * A manually entered expiry date is a *reminder*, not proof. It is stored
 * separately and labelled as operator-entered metadata, and it never
 * overrides what the provider itself reports.
 */
export function getProviderConfig() {
  const settings = loadSettings();
  return {
    workerUrl: settings.workerUrl ?? '',
    veoModel: settings.veoModel,
    veoResolution: settings.veoResolution,
    veoDurationSeconds: settings.veoDurationSeconds,
    providerId: settings.providerId,
  };
}

export function describeCredentialStatus(providerStatus, operatorNote) {
  if (!providerStatus) return { state: 'unknown', label: 'Not checked', detail: 'Provider status has not been queried yet.' };
  if (providerStatus.state === 'unavailable') {
    return { state: 'unavailable', label: 'Not configured', detail: providerStatus.detail ?? 'No Worker URL is configured.' };
  }
  if (providerStatus.state === 'ready') {
    return {
      state: 'ready',
      label: 'Worker reachable',
      detail: operatorNote
        ? `Last verified by the Worker at ${new Date(providerStatus.checkedAt).toLocaleString()}. Operator note: ${operatorNote}`
        : `Verified by the Worker at ${new Date(providerStatus.checkedAt).toLocaleString()}.`,
    };
  }
  return { state: 'error', label: 'Error', detail: providerStatus.detail ?? 'The Worker responded with an error.' };
}
